import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type GlobalProductOverrideConfig, type OrderCostConfig, type GatewayCostConfig, type ShippingCostConfig, type VariableCostConfig, type FixedCostConfig, type CostConfig, COST_TYPE } from '@adtrackify/at-tracking-event-types';

import { CostsApiService } from '@/features/costs/api/costs-api-service';
import * as Logger from '@/lib/monitoring/logger';

const getCostConfigOrNull = async <T extends CostConfig>(
  pixelId: string,
  costType: COST_TYPE
) => {
  try {
    return await CostsApiService.getCostConfig<T>(pixelId, costType);
  } catch {
    return null;
  }
};

import type { CostConfigsStore, CostConfigsState } from '../types/use-cost-configs-store-types';

interface FetchConfigsOptions {
  forceRefresh?: boolean;
}

const initialState: CostConfigsState = {
  globalProductOverride: null,
  orderConfig: null,
  gatewayConfig: null,
  shippingConfig: null,
  variableConfig: null,
  fixedConfig: null,
  isLoading: false,
  error: null,
  lastFetchedPixelId: null,
};

// Lets a response tell whether a newer fetch or a reset has replaced it.
let latestRequestId = 0;

export const useCostConfigsStore = create<CostConfigsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchConfigs: async (pixelId: string, options?: FetchConfigsOptions) => {
        if (!pixelId) return;

        const { forceRefresh = false } = options ?? {};

        // Skip if already fetched for this pixel and no error (unless forceRefresh)
        if (!forceRefresh && get().lastFetchedPixelId === pixelId && !get().error) return;

        latestRequestId += 1;
        const requestId = latestRequestId;
        set({ isLoading: true, error: null });

        try {
          const [globalRes, orderRes, gatewayRes, shippingRes, variableRes, fixedRes] =
            await Promise.all([
              getCostConfigOrNull<GlobalProductOverrideConfig>(
                pixelId,
                COST_TYPE.GLOBAL_PRODUCT_OVERRIDE
              ),
              getCostConfigOrNull<OrderCostConfig>(pixelId, COST_TYPE.ORDER),
              getCostConfigOrNull<GatewayCostConfig>(pixelId, COST_TYPE.GATEWAY),
              getCostConfigOrNull<ShippingCostConfig>(pixelId, COST_TYPE.SHIPPING),
              getCostConfigOrNull<VariableCostConfig>(pixelId, COST_TYPE.VARIABLE),
              getCostConfigOrNull<FixedCostConfig>(pixelId, COST_TYPE.FIXED),
            ]);

          if (requestId !== latestRequestId) return;

          set({
            globalProductOverride: globalRes?.data ?? null,
            orderConfig: orderRes?.data ?? null,
            gatewayConfig: gatewayRes?.data ?? null,
            shippingConfig: shippingRes?.data ?? null,
            variableConfig: variableRes?.data ?? null,
            fixedConfig: fixedRes?.data ?? null,
            isLoading: false,
            lastFetchedPixelId: pixelId,
          });
        } catch (error) {
          Logger.warn('Failed to fetch cost configs', { error, pixelId });
          if (requestId !== latestRequestId) return;
          set({ error: error as Error, isLoading: false });
        }
      },

      invalidate: () => {
        latestRequestId += 1;
        set({ ...initialState });
      },
    }),
    { name: 'cost-configs-store', enabled: process.env.NODE_ENV === 'development' }
  )
);
